
import { Vue, Component, Watch } from "vue-property-decorator";
import { IDocumentBatch, IDocumentBatchPage, IOrderLookupResult, ICrnLookupResult } from "@/store/types";
import Select from "@/components/form/Select.vue";
import notifications from "@/lib/notifications";
import { getModule } from "vuex-module-decorators";
import OrderModule from "@/store/modules/order";
import BatchIndexModule from "@/store/modules/batchIndex";
import OpenSeadragon from 'openseadragon';

@Component({
    components: {
      Select, 
    }
  })

@Component
export default class DocumentBatchIndexing extends Vue {
    orderModule = getModule(OrderModule);
    batchIndexModule = getModule(BatchIndexModule);
    batch: IDocumentBatch = null;
    pageIndex: number = 0;
    orderLookupCache: any = [];
    appHeight: number = 0;    
    imageUrl: string = "";
    imageLoading: boolean = true;
    imageDownloading: boolean = false;

    // Page index model
    pageType: boolean | null = true;
    documentType: string = "POD";
    orderNumber: string = "";
    referenceNumber: string = "";
    bolNumber: string = "";
    billTo = "";
    poNumber = "";

    showCancelModal: boolean = false;
    showCommitModal: boolean = false;
    showLookupModal: boolean = false;
    showHelpModal: boolean = false;
    submitBatchEnabled: boolean = false;

    crnLookup: string = "";
    billToLookup: string = "";
    manufacturerLookup: string = "";
    lookupResults: ICrnLookupResult[] = [];
    crnLookupRunning: boolean = false;

    image: HTMLImageElement;
    viewer: OpenSeadragon;
    zoomRate: number = 5;
    
    $refs!: {
        orderNumber: HTMLInputElement;
        zoomRateSlider: HTMLInputElement;
    };

    get documents() {
        return this.batch?.pages;
    }

    get documentTypes() {
        return [ {text: "Proof of Delivery", value: "POD"} ];
    }

    get documentCount() : number {
        if (!this.documents) return 0;
        return this.documents.filter(x => x.isStart).length;
    }

    get submitTitle() : string {
        if (!this.submitBatchEnabled) return "All documents must be indexed.";
        return "";
    }
 
    async mounted () {
        this.calculateHeight();
        this.$nextTick(() => {
            window.addEventListener('resize', this.calculateHeight);
            window.addEventListener('keyup', this.handleKeyUp);
            window.addEventListener('keydown', this.handleKeyDown);
            this.$refs.orderNumber.focus();
        })

        this.viewer = OpenSeadragon({
                    id: "displayImage",
                    showNavigationControl: false,
                    minZoomLevel: .2,
                    maxZoomLevel: 3,
                });
        this.viewer.addHandler("zoom", this.onViewerZoom, {eventType: "viewer zoom"});
    }

    suspendZoom: boolean = false;

    onViewerZoom(event, obj) {
        if (this.suspendZoom) return;

        this.suspendZoom = true;
        var zoomRate = Math.round(((event.zoom - 0.2) / 0.065));
        this.zoomRate = zoomRate;
        this.suspendZoom = false;
    }

    onZoomRateChange(event) {
        if (this.suspendZoom) return;

        this.suspendZoom = true;
        var slider = this.$refs.zoomRateSlider;
        var zoomRate = Math.round(parseInt(slider.value));
        this.zoomRate = zoomRate;

        var zoom = zoomRate * 0.065 + 0.2;
        this.viewer.viewport.zoomTo(zoom, null, true);
        this.suspendZoom = false;
    }   

    async created() {
        await this.loadBatch();
    }

    beforeDestroy() {
        window.removeEventListener('keyup', this.handleKeyUp)
    }

    get currentDocument() : IDocumentBatchPage {
        if (this.pageIndex > this.documents.length) return null;
        return this.documents[this.pageIndex];
    }

    async loadBatch()  {
        try {
            var workItemId = parseInt(this.$route.params.workItemId);
            var payload = { id: workItemId, lockBatch: true };
            var result = await this.batchIndexModule.getBatchTaskWorkItem(payload)
                .then(result =>  {
                    if (result.data.lockFailed) {
                        notifications.error("Load Error", "Batch locked by another user.");
                        this.$router.push(`/dms/batch`);
                        return false;
                    } else {
                        this.batch = result.data.batch;
                        return true;
                    }
                })
                .catch((error) => {
                    notifications.error("Load Error", "Unable to load batch.", error);
                    this.$router.push(`/dms/batch`);
                    return false;
                })

            if (result) {
                var doc = this.batch.pages[0];
                this.documentType = doc.pageType ? doc.pageType : "POD";
                this.orderNumber = doc.orderNumbers?.join(" ") ?? "";
                this.referenceNumber = doc.referenceNumbers?.join(" ") ?? "";
                this.bolNumber = doc.bolNumbers?.join(" ") ?? "";
                this.billTo = doc.billTo;
                this.poNumber = doc.poNumbers?.join(" ") ?? "";
                
                this.batch.pages.forEach((doc) => {
                    if (doc.pageType) doc.isValid = true;
                });

                await this.setPage(0);
                this.setImageUrl();
            } 
        } catch (ex) {
            notifications.error("Loading Failed", "Unable to load batches.", ex);
            console.log(ex.message);
        } 
    }

    calculateHeight() {
        var body = document.body,
            html = document.documentElement,
            header = document.querySelector(".fixed-header");

        var totalHeight = Math.max( body.scrollHeight, body.offsetHeight, 
            html.clientHeight, html.scrollHeight, html.offsetHeight);

        this.appHeight = totalHeight - header.clientHeight;
    }

    getDocumentName(document: IDocumentBatchPage): string {
        if (document.isDeleted || !document.isStart ) return "";
        return `${document.pageType} ${document.orderNumbers?.join(' ') ?? ""}`;
    }
       
    getPageListItemClass(document: IDocumentBatchPage) {
        var result = "";
        if ((this.pageIndex + 1) === document.ordinal) result += "document-current "
        if (document.isValid == false) result += "document-invalid ";

        if (document.isStart) result += "document-start ";
        if (document.isValid !== undefined) result += "document-icon ";
        return result.trim();
    }
    
    getToggleStyle(option: boolean | null): string {
        if(option === null && this.pageType == null) return "toggle-option toggle-deleted";
        return `toggle-option${option===this.pageType?" toggle-selected":""}`;
    } 
    
    getImagePanelStyle(): string {
        return `height:${this.appHeight - 63}px`;
    }

    getImageStyle(): string {
        return `height:${this.appHeight - 90}px;${this.imageLoading?"visibility:hidden":""}`;
    }

    getLookupRowClass(result: ICrnLookupResult) : string {
        return result.isSelected ? "selected" : "";
    }

    onLookupRowClicked(result: ICrnLookupResult) {
        result.isSelected = !result.isSelected;
    }

    getSubmitBatchEnabled(): boolean {
        const firstInvalid = this.documents.find(function (document) {
            return document.isValid !== true || (document.isDeleted === false && (document.orderNumbers.length === 0 || document.orderNumbers[0] === ""));
        });

        var result = (typeof firstInvalid === 'undefined') || firstInvalid === null;
        return result;
    }

    async setPageType(value: boolean | null) {
        let first = this.getFirstNonDeletedIndex();

        // Prevent first non-deleted from being an attachment
        if (value === false && this.pageIndex <= first) return;

        const doc = this.currentDocument;
        doc.isStart = value;
        doc.isDeleted = (value === null);

        if (value === true) {
            doc.orderNumbers = [];
            this.orderNumber = "";
            this.$refs.orderNumber.focus();
        }
        
        // Ensure the first non-deleted is not an attachment
        first = this.getFirstNonDeletedIndex();
        if (first != -1) this.documents[first].isStart = true;

        if (first == this.pageIndex && value === false) {
            this.pageType = true;

        } else {
            this.pageType = value;
        }

        this.submitBatchEnabled = this.getSubmitBatchEnabled();

        // Attempt to go to next page if deleted
        if( value === null) {
            await this.setPage(this.pageIndex + 1);
        }
    }

    getFirstNonDeletedIndex() : number {
        const first = this.documents.find(function (document) {
            return !document.isDeleted;
        });

        if (!first) return -1;
        return first.ordinal - 1;
    }

    getPriorNonDeletedIndex(index: number) : number {
        if (index === 0) return -1;
        for(let i = index - 1; i >= 0; i--) {
            if (!this.documents[i].isDeleted) return i;
        }
        return -1;
    }

    setImageUrl() {
        if (!this.batch?.id || this.batch?.id === 0) return;
        this.imageLoading = true;

        var payload = { id: this.batch.id, page: this.pageIndex};
        this.batchIndexModule.getBatchDocumentImage(payload)
        .then(result =>  {
            this.imageUrl = result.data.url;
            var tileSource = { type: "image", url: this.imageUrl };
            this.viewer.open(tileSource);
            this.viewer.viewport.setRotation(this.currentDocument.rotate??0, true);
            this.imageLoading = false;
        })
        .catch((error) => {
            console.log(error);
            notifications.error("Load Failed", "Unable to load image.", error);
            this.imageLoading = false;
        })
    }

    onOrderNumberChange() {
        this.orderNumber = this.orderNumber.trim().replace('\n', '');
    }

    async handleKeyDown(e: KeyboardEvent) {
        if (e.code === "PageDown" || e.code === "PageUp") e.preventDefault();
    }

    async handleKeyUp(e: KeyboardEvent) {
        if (this.showLookupModal || this.showCommitModal) return;

        // Switch three-way page type toggle.  Page type can be true, false, or null
        if (e.code === "KeyP" && e.altKey) {
            this.setPageType(!this.pageType);
        } 

        if (e.code === "Delete" && e.altKey) {
            this.setPageType(null);
        } 

        if (e.code === "PageUp") {
            e.preventDefault();
            await this.setPage(this.pageIndex - 1);
        }

        if (e.code === "PageDown" || e.code === "Enter" || e.code === "NumpadEnter") {
            e.preventDefault();
            await this.setPage(this.pageIndex + 1);
        }
    }

    async onDocumentClicked(document: IDocumentBatchPage) {
        await this.setPage(document.ordinal - 1);
    }

    async setPage(pageIndex: number) {
        // Attempt to look up order data
        var lookupResult : IOrderLookupResult[] = [];
        try{
            if (this.orderNumber) {
                const orderNumbersKey = this.orderNumber.replace('\n', '');
                const cachedValue = this.orderLookupCache.find((x) => x.key === orderNumbersKey)

                if (cachedValue){
                    lookupResult = cachedValue.value;
                } else {
                    const payload = {
                        orderNumbers: orderNumbersKey.split(" ")
                    };
    
                    lookupResult = await this.batchIndexModule.getOrderLookup(payload);
                    this.orderLookupCache.push({key: orderNumbersKey, value: lookupResult});

                    this.referenceNumber = lookupResult.map(x => x.crn).join(',');
                    this.billTo = lookupResult[0]?.billTo ?? "";
                    this.poNumber = lookupResult.map(x => x.po).join(',');
                    this.bolNumber = lookupResult.map(x => x.bol).join(',');
                }    
            }
        } catch (ex) {
            notifications.error("Order Lookup Error", "Error while looking up order number.")
        }

        let doc = this.currentDocument;
        doc.pageType = this.documentType;
        doc.orderNumbers = this.orderNumber.toString().trim().split(" ");
        doc.referenceNumbers = this.referenceNumber.toString().split(" ");
        doc.bolNumbers = this.bolNumber.toString().split(" ");
        doc.billTo = this.billTo;
        doc.poNumbers = this.poNumber.toString().split(" ");

        if ((!lookupResult || !doc.orderNumbers || !doc.orderNumbers.some(x => x !== '')) && doc.ordinal != pageIndex + 1 && doc.isStart) {
            doc.isValid = false;
        } else {
            const invalid = lookupResult.filter(x => x.isValid === false); 
            doc.isValid = invalid.length === 0;

            if (invalid.length > 0) {
                notifications.display("Invalid Order Numbers", `The following CDS order numbers are not valid: ${invalid.map(x => x.orderNumber).join(", ")}`, "warning");
            }
        }
       
        this.submitBatchEnabled = this.getSubmitBatchEnabled();

        if (pageIndex < 0 || pageIndex >= this.documents.length) {
            return;
        }

        this.pageIndex = pageIndex;
        doc = this.documents[this.pageIndex];

        this.setImageUrl();

        if (doc.isDeleted) this.pageType = null;
        if (!doc.isDeleted) this.pageType = doc.isStart;

        if (doc.isStart) {
            this.documentType = doc.pageType ? doc.pageType : "POD";
            this.orderNumber = doc.orderNumbers?.join(" ") ?? "";
            this.referenceNumber = doc.referenceNumbers?.join(" ") ?? "";
            this.bolNumber = doc.bolNumbers?.join(" ") ?? "";
            this.billTo = doc.billTo;
            this.poNumber = doc.poNumbers?.join(" ") ?? "";
        } else {
            const priorDocIndex = this.getPriorNonDeletedIndex(pageIndex);
            const priorDoc = this.documents[priorDocIndex];

            if (priorDoc) {
                this.documentType = priorDoc?.pageType ? priorDoc.pageType : "POD";
                this.orderNumber = priorDoc.orderNumbers.join(" ");
                this.referenceNumber = priorDoc.referenceNumbers?.join(" ") ?? "";
                this.bolNumber = priorDoc.bolNumbers?.join(" ") ?? "";
                this.billTo = priorDoc.billTo;
                this.poNumber = priorDoc.poNumbers?.join(" ") ?? "";
            } else {
                doc.isStart = true; 
                this.pageType = true;
                this.documentType = "POD";
                this.orderNumber = "";
                this.referenceNumber = "";
                this.bolNumber = "";
                this.billTo = "";
                this.poNumber = "";
            }
        }
    }

    showHelp() {
        this.showHelpModal = true;
    }

    rotateLeft() {
        this.currentDocument.rotate = (this.currentDocument.rotate??0) - 90;
        if (this.currentDocument.rotate < 0) this.currentDocument.rotate = 270;
        this.viewer.viewport.setRotation(this.currentDocument.rotate);
    }

    rotateRight() {
        this.currentDocument.rotate = (this.currentDocument.rotate??0) + 90;
        if (this.currentDocument.rotate >= 360) this.currentDocument.rotate = 0;
        this.viewer.viewport.setRotation(this.currentDocument.rotate);
    }

    async incrementPage() {
        await this.setPage(this.pageIndex - 1);
    }

    async decrementPage() {
        await this.setPage(this.pageIndex + 1);
    }

    async downloadImage() {
        if (this.imageDownloading) return;

        this.imageDownloading = true;

        var link = document.createElement("a");
        link.href = this.imageUrl;
        link.target = "_blank";
        link.download = `page-${this.batch.id}-${this.pageIndex + 1}.jpg`;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
            link.parentNode.removeChild(link);
        }, 0);

        this.imageDownloading = false;
        notifications.success("Download Complete", "Page downloaded.");
    }

    showCancelConfirmation() {
        this.showCancelModal = true;
    }

    showSubmitConfirmation() {
        this.showCommitModal = true;
    }

    async cancelAccept() {
        this.showCancelModal = false;

        var workItemId = parseInt(this.$route.params.workItemId);
        let payload = {
            id: workItemId,
            batch: this.batch,
            unlockBatch: true,
            saveBatch: false,
            targetQueueId: null
        }

        await this.batchIndexModule.putBatchTaskWorkItem(payload)
            .then(result =>  {
                notifications.success("Batch Canceled", "Batch has been canceled and unlocked.")
                this.$router.push(`/dms/batch`);
            })
            .catch((error) => {
                notifications.error("Cancel Failed", "Unable to cancel batch.", error);
            })
    }

    cancelCancel() {
        this.showCancelModal = false;
    }

    async commitAccept(launchNext: boolean) {
        this.showCommitModal = false;
        var workItemId = parseInt(this.$route.params.workItemId);
        let payload = {
            id: workItemId,
            batch: this.batch,
            unlockBatch: true,
            saveBatch: true,
            targetQueueId: 2
        }

        await this.batchIndexModule.putBatchTaskWorkItem(payload)
            .then(result =>  {
                notifications.success("Batch Saved", "Completed batch has been saved.")
                if (launchNext) this.$router.push(`/dms/batch/${result.nextWorkItemId}`);
                else this.$router.push(`/dms/batch`);
            })
            .catch((error) => {
                console.log('Error', error);
                notifications.error("Save Failed", "Unable to save batch.", error);
            })
    }

    commitCancel() {
        this.showCommitModal = false;
    }

    async saveBatch() {
        var workItemId = parseInt(this.$route.params.workItemId);
        let payload = {
            id: workItemId,
            batch: this.batch,
            unlockBatch: true,
            saveBatch: true,
            targetQueueId: null
        }

        await this.batchIndexModule.putBatchTaskWorkItem(payload)
            .then(result =>  {
                notifications.success("Batch Saved", "Batch changes have been saved.");
                this.$router.push(`/dms/batch`);
            })
            .catch((error) => {
                notifications.error("Save Failed", "Unable to save batch.", error);
            })
    }
    
    promptLookupDialog() {
        this.crnLookup = "";
        this.lookupResults = [];
        this.showLookupModal = true;
    }

    onlyUnique(value, index, array) {
       return array.indexOf(value) === index;
    }

    lookupAccept() {
        var newOrders = this.lookupResults.filter(x => x.isSelected).map(x => x.orderNumber)
        this.orderNumber = this.orderNumber.split(' ').concat(newOrders).filter(this.onlyUnique).join(' ');
        this.showLookupModal = false;
    }

    lookupCancel() {
        this.showLookupModal = false;
    }

    setFieldFocus() {
        this.$nextTick(() => {
            this.$refs.orderNumber.focus();
        })
    }

    async performCrnLookup() {
        if (!this.billToLookup?.trim() && !this.manufacturerLookup?.trim()) {
            notifications.display("Required", "Please include the Bill To and/or Manufcturer to find orders.", "warning");
            return;
        }

        if (!this.crnLookup?.trim()) {
            notifications.display("Required", "Please include the CRN to find orders.", "warning");
            return;
        }

        this.crnLookupRunning = true;

        const payload = {
            crn: this.crnLookup,
            billTo: this.billToLookup,
            manufacturer: this.manufacturerLookup,
            facility: this.batch.facility
        };
    
        try{
            this.lookupResults = await this.batchIndexModule.getCrnLookup(payload);
        } catch (ex) {
            notifications.error("CRN Lookup Failed", "Unable to perform order lookup by CRN.");
        } finally {
            this.crnLookupRunning = false;
        }
    }
}
